/**
 * overlayBackButton — o botão Voltar fecha o modal, não a tela.
 *
 * PROBLEMA (A-3): com um ou dois modais empilhados, Voltar não fazia nada — a
 * URL continuava /perfil e os diálogos seguiam montados (`data-scroll-locked="2"`).
 * No Android isso é grave por dois motivos: o botão físico é COMO as pessoas
 * fecham coisas, e no WebView o mesmo gesto pode sair do app com o modal aberto.
 *
 * SOLUÇÃO: cada camada de modal aberta empilha um estado sentinela no history.
 * Voltar consome o sentinela e fecha a camada do TOPO, sem navegar. Quando a
 * mãe fecha pelo X/Esc, o sentinela correspondente é consumido em silêncio para
 * o history não acumular entradas mortas.
 *
 * Fecha por `Escape` sintético em vez de mexer no state de cada modal: é assim
 * que o Radix/vaul fecham a camada do topo da própria pilha, respeitando o
 * aninhamento (fecha o CompromissoModal, não a Agenda embaixo dele). Para
 * overlays custom que não escutam Escape, cai no overlayManager.closeTop().
 */

import { overlayManager } from "./overlayManager";
import { OVERLAY_OPEN_ATTR } from "./overlayPointerGuard";

const OPEN_LAYER_SELECTOR = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[data-vaul-drawer][data-state="open"]',
  `[${OVERLAY_OPEN_ATTR}="true"]`,
].join(", ");

const SENTINEL_KEY = "__m360OverlayDepth";

function countOpenLayers(): number {
  try {
    return document.querySelectorAll(OPEN_LAYER_SELECTOR).length;
  } catch {
    return 0;
  }
}

let installed = false;

/**
 * Ida para /maternar/planos em curso (useOpenPlanos).
 *
 * Ali as janelas fecham DEPOIS do navigate, e o history.back() que devolve a
 * marca de cada janela chegava depois e desfazia a navegação. Enquanto a ida
 * estiver em curso, as marcas das janelas fechadas são só esquecidas (ficam no
 * history como entradas de /perfil, para onde o Voltar levaria de todo jeito).
 *
 * Termina por CONDIÇÃO, não por tempo:
 *  - a rota é /maternar/planos e não sobrou janela aberta; ou
 *  - a rota mudou para outro lugar que não a de origem nem a de planos; ou
 *  - a mãe apertou Voltar (popstate dela).
 * O tempo de 10 s é só rede de segurança, caso nenhuma condição aconteça.
 *
 * O Voltar físico NUNCA é engolido: a supressão só evita o history.back() que
 * nós mesmos disparamos; o popstate da mãe segue o caminho normal.
 */
const ROTA_PLANOS = "/maternar/planos";
const REDE_DE_SEGURANCA_MS = 10_000;
let idaParaPlanos: { origem: string; ate: number } | null = null;

export function marcarIdaParaPlanos(): void {
  if (typeof window === "undefined") return;
  idaParaPlanos = { origem: window.location.pathname, ate: Date.now() + REDE_DE_SEGURANCA_MS };
}

/** Só para testes. */
export function __idaParaPlanosEmCurso(): boolean {
  return idaParaPlanos !== null;
}

function idaEmCurso(open: number): boolean {
  if (!idaParaPlanos) return false;
  const rota = window.location.pathname;
  const naPlanos = rota.startsWith(ROTA_PLANOS);
  const foiParaOutroLugar = !naPlanos && rota !== idaParaPlanos.origem;
  if ((naPlanos && open === 0) || foiParaOutroLugar || Date.now() > idaParaPlanos.ate) {
    idaParaPlanos = null;
    // Ainda vale para ESTA chamada quando acabamos de chegar: as marcas das
    // janelas que acabaram de fechar não podem virar history.back().
    return naPlanos;
  }
  return true;
}

export function installOverlayBackButton(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  /** Quantos sentinelas nós empilhamos no history. */
  let pushed = 0;
  /** Voltas que nós mesmos disparamos — o popstate delas não fecha nada. */
  let selfPops = 0;

  const sync = () => {
    const open = countOpenLayers();

    while (pushed < open) {
      pushed += 1;
      try {
        window.history.pushState({ [SENTINEL_KEY]: pushed }, "");
      } catch {
        pushed -= 1;
        break;
      }
    }

    if (pushed > open && idaEmCurso(open)) {
      pushed = open;
      return;
    }
    if (idaParaPlanos) idaEmCurso(open);

    // Modal fechado pelo X/Esc: devolve o sentinela para o history não crescer.
    while (pushed > open) {
      pushed -= 1;
      selfPops += 1;
      try {
        window.history.back();
      } catch {
        selfPops -= 1;
        break;
      }
    }
  };

  const onPopState = () => {
    if (selfPops > 0) {
      selfPops -= 1;
      return;
    }
    // Voltar da mãe: encerra a ida aos planos e segue o caminho normal.
    idaParaPlanos = null;

    if (countOpenLayers() === 0) return; // nada aberto: navegação normal

    // Nosso sentinela acabou de ser consumido pelo Voltar.
    if (pushed > 0) pushed -= 1;

    // Fecha a camada do topo do jeito que o próprio Radix/vaul fecha.
    document.dispatchEvent(
      new KeyboardEvent("keydown", { key: "Escape", code: "Escape", bubbles: true, cancelable: true }),
    );

    // Overlay custom que não escuta Escape: garante o fechamento.
    window.setTimeout(() => {
      if (countOpenLayers() > 0) overlayManager.closeTop();
      sync();
    }, 120);
  };

  window.addEventListener("popstate", onPopState);
  overlayManager.subscribe(sync);
  // Modais Radix não passam pelo manager: observa o DOM para pegar todos.
  const observer = new MutationObserver(() => sync());
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ["data-state", OVERLAY_OPEN_ATTR],
    childList: true,
    subtree: true,
  });
}
